import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

const MODES = {
  explore: { label: '📖 Explore Lore', key: 'explore' },
  develop: { label: '✍️ Develop Episode', key: 'develop' },
  compose: { label: '🎵 Compose Song', key: 'compose' },
};

// ── Full character roster ─────────────────────────────────────────
const CHARACTERS = [
  // Core Team Consciousness
  'Fho', 'Lief', 'Ling', 'Ti', 'Uncle Emit', 'Gene', 'Maya',
  // Villains
  'The Doubter', 'Tyrannus', 'Enigmus', 'Unitas the Amalgamation',
  // Wisdom & Performance Coaches
  'SAGE', 'SHIP', 'CHAMP',
  // Family Coach Personas
  'Valor', 'Ama',
  // Wellness Coach
  'SAFE',
];

// ── Time Formatting Helper ────────────────────────────────────────
const formatLocalTime = (utcString) => {
  if (!utcString) return '';
  try {
    const date = new Date(utcString + 'Z'); // Ensure UTC interpretation
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } catch { return utcString?.slice(0, 16) || ''; }
};

// ── Voice Hook ────────────────────────────────────────────────────
function useVoiceChat() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('chroniclesStudioVoiceEnabled');
    return saved ? JSON.parse(saved) : false;
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const lastSpokenRef = useRef('');
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  const isSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  useEffect(() => {
    if (!isSupported) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += t;
      }
      if (finalTranscript) setTranscript(finalTranscript);
    };
    recognitionRef.current.onend = () => setIsListening(false);
    recognitionRef.current.onerror = () => setIsListening(false);
    return () => { if (recognitionRef.current) recognitionRef.current.abort(); };
  }, [isSupported]);

  const startListening = () => {
    if (!isSupported || !recognitionRef.current) return;
    setTranscript('');
    setIsListening(true);
    try { recognitionRef.current.start(); } catch (e) {}
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setIsListening(false);
  };

  const stripMarkdownForVoice = (text) => text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/FHO/gi, 'foe');

  const speakAtRate = (text, rate = 0.90) => {
    if (!synthRef.current || !voiceEnabled) return;
    synthRef.current.cancel();
    setIsPaused(false);
    lastSpokenRef.current = text;
    const cleanText = stripMarkdownForVoice(text);
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = rate;
    utterance.pitch = 0.85;
    const voices = synthRef.current.getVoices();
    const preferredVoice = voices.find(v =>
      v.name.includes('Samantha') || v.name.includes('Karen') || v.name.includes('Victoria')
    );
    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.onstart = () => { setIsSpeaking(true); setIsPaused(false); };
    utterance.onend = () => { setIsSpeaking(false); setIsPaused(false); };
    synthRef.current.speak(utterance);
  };

  const speak = (text) => speakAtRate(text, 0.90);
  const pauseSpeaking = () => { if (synthRef.current && isSpeaking) { synthRef.current.pause(); setIsPaused(true); } };
  const resumeSpeaking = () => { if (synthRef.current && isPaused) { synthRef.current.resume(); setIsPaused(false); } };
  const rereadLast = () => { if (lastSpokenRef.current) speakAtRate(lastSpokenRef.current, 0.90); };
  const skipSpeaking = () => { if (synthRef.current) { synthRef.current.cancel(); setIsSpeaking(false); setIsPaused(false); } };
  const stopSpeaking = () => { if (synthRef.current) { synthRef.current.cancel(); setIsSpeaking(false); setIsPaused(false); } };

  const toggleVoice = () => {
    const newValue = !voiceEnabled;
    setVoiceEnabled(newValue);
    localStorage.setItem('chroniclesStudioVoiceEnabled', JSON.stringify(newValue));
    if (!newValue) stopSpeaking();
  };

  return {
    isListening, transcript, voiceEnabled, isSpeaking, isPaused, isSupported,
    startListening, stopListening, speak, stopSpeaking, toggleVoice,
    pauseSpeaking, resumeSpeaking, rereadLast, skipSpeaking,
  };
}

// ── Main Component ────────────────────────────────────────────────
export default function ChroniclesStudioChat() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('explore');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showTeamHistory, setShowTeamHistory] = useState(false);
  const [teamConversations, setTeamConversations] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [studioName, setStudioName] = useState('Carlton');
  const [vectorCount, setVectorCount] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const wasListeningRef = useRef(false);
  const [musicGenerating, setMusicGenerating] = useState(false);
  const [musicUrl, setMusicUrl] = useState(null);
  const [musicError, setMusicError] = useState('');
  const [storedSunoPrompt, setStoredSunoPrompt] = useState(null);
  const [tuning528, setTuning528] = useState(false);
  const [tunedUrl, setTunedUrl] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState('Fho');
  const [vocalUrls, setVocalUrls] = useState({});
  const [isGeneratingVocals, setIsGeneratingVocals] = useState(false);
  const [vocalError, setVocalError] = useState('');

  const voice = useVoiceChat();

  const voiceEnabledRef = useRef(voice.voiceEnabled);
  const voiceSpeakRef   = useRef(voice.speak);
  useEffect(() => { voiceEnabledRef.current = voice.voiceEnabled; }, [voice.voiceEnabled]);
  useEffect(() => { voiceSpeakRef.current   = voice.speak; },        [voice.speak]);

  const userEmail = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').email || ''; }
    catch { return ''; }
  })();

  useEffect(() => {
    if (!userEmail) { navigate('/login'); return; }
    document.body.style.background = '#0a0f1e';
    document.documentElement.style.background = '#0a0f1e';
    loadProfile();
    loadStats();
    startNewConversation();
    return () => {
      document.body.style.background = '';
      document.documentElement.style.background = '';
    };
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (wasListeningRef.current && !voice.isListening && voice.transcript.trim()) setInput(voice.transcript);
    wasListeningRef.current = voice.isListening;
  }, [voice.isListening, voice.transcript]);

  useEffect(() => { if (voice.transcript) setInput(voice.transcript); }, [voice.transcript]);

  const loadProfile = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/profile?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.exists && data.profile) { setStudioName(data.profile.studio_name || 'Carlton'); }
      else { navigate('/mindstreams-studio/setup'); }
    } catch { navigate('/mindstreams-studio/setup'); }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/stats?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setVectorCount(data.vector_count ?? 0);
    } catch { setVectorCount(0); }
  };

  const startNewConversation = async (newMode = null) => {
    const m = newMode || mode;
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/new-conversation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, mode: m }),
      });
      const data = await res.json();
      setConversationId(data.conversation_id);
      setMessages([]);
      setInput('');
    } catch (err) { console.error('Failed to start new conversation:', err); }
  };

  const loadConversations = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/conversations?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setConversations(data.conversations || []);
      // Check admin status
      setIsAdmin(data.is_admin || false);
    } catch { setConversations([]); }
  };
  const loadTeamConversations = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/team-conversations?user_email=${encodeURIComponent(userEmail)}`);
      if (res.ok) {
        const data = await res.json();
        setTeamConversations(data.conversations || []);
      }
    } catch (err) { console.error("Failed to load team conversations:", err); }
  };

  const switchMode = async (newMode) => { setMode(newMode); await startNewConversation(newMode); };
  const closeMenu  = (e) => { if (e && e.stopPropagation) e.stopPropagation(); setShowMenu(false); };
  const exportPdf = useCallback(async () => {
    if (!conversationId) {
      alert('Start a conversation first — there needs to be content to export.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/v1/script-export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          conversation_id: conversationId,
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        alert(`Export failed: ${err}`);
        return;
      }
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename=\"([^\"]+)\"/);
      const filename = m ? m[1] : 'chronicles_export.pdf';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export error: ${e.message}`);
    }
  }, [conversationId, userEmail]);

  const sendMessage = useCallback(async (overrideInput) => {
    const trimmed = (overrideInput || input).trim();
    if (!trimmed || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    let fullResponse = '';
    try {
      abortRef.current = new AbortController();
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: abortRef.current.signal,
        body: JSON.stringify({ message: trimmed, conversation_id: conversationId, mode, user_email: userEmail }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') break;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.conversation_id && !conversationId) setConversationId(parsed.conversation_id);
            if (parsed.text) {
              fullResponse += parsed.text;
              setMessages(prev => {
                const updated = [...prev];
                const last = { ...updated[updated.length - 1] };
                last.content += parsed.text;
                updated[updated.length - 1] = last;
                return updated;
              });
            }
            if (parsed.error) throw new Error(parsed.error);
          } catch { /* ignore parse errors */ }
        }
      }
      if (voiceEnabledRef.current && fullResponse) setTimeout(() => voiceSpeakRef.current(fullResponse), 300);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          const last = { ...updated[updated.length - 1] };
          last.content = 'Sorry, something went wrong. Please try again.';
          last.error = true;
          updated[updated.length - 1] = last;
          return updated;
        });
      }
    } finally {
      setMessages(prev => {
        const updated = [...prev];
        const last = { ...updated[updated.length - 1] };
        last.streaming = false;
        updated[updated.length - 1] = last;
        return updated;
      });
      setLoading(false);
      inputRef.current?.focus();
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant') {
          const m = last.content.match(/Suno\/Udio Prompt[^\n]*\n?([^\n]{20,})/i);
          if (m) setStoredSunoPrompt(m[1].trim());
        }
        return prev;
      });
    }
  }, [input, loading, conversationId, mode, userEmail]);

  const generateMusic = async (prompt, fullContent) => {
    setMusicGenerating(true); setMusicUrl(null); setMusicError('');
    const verseCount = (fullContent.match(/\[Verse/gi) || []).length;
    const hasBridge = /\[Bridge/i.test(fullContent);
    const enrichedPrompt = `${prompt}, song structure: ${verseCount} verses${hasBridge ? ', bridge' : ''}, chorus, outro`;
    let duration = 30;
    const durPatterns = [/(\d{2,3})\s*(?:second|sec)/i, /[Dd]uration[:\s]+(\d{2,3})/, /(\d{2,3})[-\s]second/i, /(?:90|60|120|180)\s*(?:sec|s\b)/];
    for (const pat of durPatterns) {
      const m = (fullContent || '').match(pat);
      if (m) { const parsed = parseInt(m[1] || m[0]); if (parsed >= 8 && parsed <= 180) { duration = parsed; break; } }
    }
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/generate-music`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, prompt: enrichedPrompt, duration }),
      });
      const data = await res.json();
      if (data.success && data.audio_url) { setMusicUrl(data.audio_url); }
      else { setMusicError('Music generation failed. Try again.'); }
    } catch { setMusicError('Connection error. Try again.'); }
    finally { setMusicGenerating(false); }
  };

  const generateVocals = async (lyrics, messageIndex) => {
    setIsGeneratingVocals(true); setVocalError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/generate-vocals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lyrics, character: selectedCharacter, user_email: userEmail }),
      });
      const data = await res.json();
      if (data.audio_url) { setVocalUrls(prev => ({ ...prev, [messageIndex]: data.audio_url })); }
      else { setVocalError('Vocal generation failed: ' + (data.detail || 'Unknown error')); }
    } catch (err) { console.error('Vocal generation error:', err); setVocalError('Vocal generation failed. Check console.'); }
    finally { setIsGeneratingVocals(false); }
  };

  const tune528 = async (filename) => {
    setTuning528(true); setTunedUrl(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/tune-528`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, filename }),
      });
      const data = await res.json();
      if (data.success) setTunedUrl(data.audio_url); else setMusicError('528 Hz tuning failed. Try again.');
    } catch { setMusicError('Tuning error. Try again.'); }
    finally { setTuning528(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const handleMicClick = () => {
    if (voice.isListening) { voice.stopListening(); if (voice.transcript.trim()) setTimeout(() => sendMessage(voice.transcript), 100); }
    else { voice.startListening(); }
  };

  return (
    <div style={s.root}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.logoIcon}>🎬</span>
          <div>
            <div style={s.headerTitle}>MindStreams Studio</div>
            <div style={s.headerSub}>THE FHO CHRONICLES</div>
          </div>
        </div>
        <div style={s.headerRight}>
          {vectorCount !== null && <span style={s.vectorBadge}>{vectorCount} vectors indexed</span>}
          <button style={s.menuBtn} onClick={(e) => { e.stopPropagation(); setShowMenu(v => !v); setShowHistory(false); }}>☰</button>
        </div>
      </div>

      {/* Mode Toggle */}
      <div style={s.modeBar}>
        {Object.values(MODES).map(m => (
          <button key={m.key} style={{ ...s.modeBtn, ...(mode === m.key ? s.modeBtnActive : {}) }} onClick={() => switchMode(m.key)}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Dropdown Menu */}
      {showMenu && (
        <div style={s.menuDropdown} onClick={e => e.stopPropagation()}>
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); startNewConversation(); }}>
            ✦ New Conversation
          </button>
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); loadConversations(); setShowHistory(true); }}>
            📋 Conversation History
          </button>
          
            <button style={s.menuItem} onClick={(e) => { closeMenu(e); loadTeamConversations(); setShowTeamHistory(true); }}>
              👥 Team Conversations
            </button>
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); navigate('/mindstreams-studio/setup'); }}>
            ⚙️ Edit Profile
          </button>
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); window.open('https://freehappyone.com/docs/FHO_Chronicles_Voice_Reference.docx', '_blank'); }}>
            🎤 Voice Reference Guide
          </button>
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); navigate('/mindstreams-studio/mixer'); }}>
            🎚️ Mixer
          </button>
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); exportPdf(); }}>
            📄 Export to PDF
          </button>
          <div style={s.menuDivider} />
          <button style={s.menuItem} onClick={(e) => { closeMenu(e); navigate('/emit-the-bible'); }}>
            ← Back to FHO Platform
          </button>
          <button style={{ ...s.menuItem, color: 'rgba(255,255,255,0.4)' }} onClick={(e) => { closeMenu(e); localStorage.removeItem('user'); navigate('/login'); }}>
            Logout
          </button>
        </div>
      )}

      {/* History Panel */}
      {showHistory && (
        <div style={s.historyPanel}>
          <div style={s.historyHeader}>
            <span>Conversation History</span>
            <button style={s.historyClose} onClick={() => setShowHistory(false)}>✕</button>
          </div>
          {conversations.length === 0 ? (
            <p style={s.historyEmpty}>No past conversations yet.</p>
          ) : conversations.map(c => (
            <div key={c.conversation_id} style={s.historyItem} onClick={async () => {
              setConversationId(c.conversation_id); setMode(c.mode || 'explore'); setShowHistory(false);
              try {
                const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/messages?user_email=${encodeURIComponent(userEmail)}&conversation_id=${c.conversation_id}`);
                const data = await res.json(); setMessages(data.messages || []);
              } catch (err) { console.error('Failed to load messages:', err); }
            }}>
              <div style={s.historyItemPreview}>{c.preview || 'Conversation'}</div>
              <div style={s.historyItemMeta}>{c.mode} · {c.message_count} messages · {formatLocalTime(c.last_message)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div style={s.messagesArea}>
        {messages.length === 0 && (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>✦</div>
            <div style={s.emptyTitle}>
              {mode === 'explore' ? 'Explore the FHO Chronicles Universe' : mode === 'develop' ? 'Develop Your Next Episode' : 'Compose a Song for the Chronicles'}
            </div>
            <div style={s.emptyDesc}>
              {mode === 'explore' ? 'Ask about characters, world-building, lore, timelines, or any aspect of your universe.'
                : mode === 'develop' ? 'Tell me what you want to create. Analyze an episode, write something new, or let me suggest what comes next.'
                : 'Write a song, compose a character theme, or let me suggest what song should come next. Every song includes full lyrics and a production brief ready for Suno or Udio.'}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} style={{ ...s.msgRow, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {msg.role === 'assistant' && <div style={s.avatarIcon}>🎬</div>}
            <div style={{ ...s.bubble, ...(msg.role === 'user' ? s.bubbleUser : s.bubbleAssistant), ...(msg.error ? s.bubbleError : {}) }}>
              <MessageContent
                content={msg.content}
                streaming={msg.streaming}
                onGenerateMusic={msg.role === 'assistant' ? generateMusic : null}
                musicGenerating={musicGenerating}
                musicUrl={musicUrl}
                musicError={musicError}
                storedSunoPromptProp={storedSunoPrompt}
                onTune528={tune528}
                tuning528={tuning528}
                tunedUrl={tunedUrl}
                messageIndex={idx}
                selectedCharacter={selectedCharacter}
                onCharacterChange={setSelectedCharacter}
                onGenerateVocals={msg.role === 'assistant' ? generateVocals : null}
                isGeneratingVocals={isGeneratingVocals}
                vocalUrl={vocalUrls[idx] || null}
                vocalError={vocalError}
              />
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={s.inputWrapper}>
        <textarea
          ref={inputRef}
          style={s.textarea}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            voice.isListening ? 'Listening...' :
            mode === 'explore' ? 'Ask about characters, lore, world-building...'
            : mode === 'develop' ? 'Describe what you want to develop...'
            : 'Describe a song, name a character, or ask for a suggestion...'
          }
          rows={2}
          disabled={loading}
        />

        {(voice.isSpeaking || voice.isPaused) && (
          <div style={s.playbackBar}>
            <button style={s.playbackBtn} onClick={voice.rereadLast} title="Re-read from beginning">↩️</button>
            <button style={s.playbackBtn} onClick={voice.isPaused ? voice.resumeSpeaking : voice.pauseSpeaking} title={voice.isPaused ? 'Resume' : 'Pause'}>
              {voice.isPaused ? '▶️' : '⏸️'}
            </button>
            <button style={s.playbackBtn} onClick={voice.skipSpeaking} title="Skip — stop reading">⏭️</button>
            <span style={s.playbackLabel}>{voice.isPaused ? 'Paused' : 'Speaking...'}</span>
          </div>
        )}

        <div style={s.buttonRow}>
          {voice.isSupported && (
            <button type="button" onClick={handleMicClick} disabled={loading}
              style={{ ...s.iconBtn, background: voice.isListening ? '#f43f5e' : 'rgba(212,175,55,0.15)', border: voice.isListening ? 'none' : '1px solid rgba(212,175,55,0.3)', boxShadow: voice.isListening ? '0 0 0 4px rgba(244,63,94,0.25)' : 'none', opacity: loading ? 0.4 : 1 }}
              title={voice.isListening ? 'Stop and send' : 'Speak your message'}>
              {voice.isListening ? '⏹️' : '🎤'}
            </button>
          )}
          {voice.isSupported && (
            <button type="button" onClick={voice.toggleVoice} disabled={loading}
              style={{ ...s.iconBtn, background: voice.voiceEnabled ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.05)', border: voice.voiceEnabled ? '1px solid rgba(212,175,55,0.6)' : '1px solid rgba(255,255,255,0.1)', opacity: loading ? 0.4 : 1 }}
              title={voice.voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}>
              {voice.voiceEnabled ? '🔊' : '🔇'}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button style={{ ...s.sendBtn, opacity: loading || !input.trim() ? 0.4 : 1 }} onClick={() => sendMessage()} disabled={loading || !input.trim()}>
            {loading ? '⟳' : '→'}
          </button>
        </div>

        {voice.isListening && (
          <div style={s.listeningIndicator}>
            <span style={s.listeningDot} />
            Listening... tap ⏹️ to send
          </div>
        )}
        <div style={s.inputHint}>Shift+Enter for new line · Enter to send</div>
      </div>
    </div>
  );
}

// ── Message Content Renderer ──────────────────────────────────────
function MessageContent({
  content, streaming, onGenerateMusic, musicGenerating, musicUrl, musicError,
  storedSunoPromptProp, onTune528, tuning528, tunedUrl,
  messageIndex, selectedCharacter, onCharacterChange,
  onGenerateVocals, isGeneratingVocals, vocalUrl, vocalError,
}) {
  if (!content && streaming) return <span style={{ color: 'rgba(212,175,55,0.6)' }}>▌</span>;
  if (!content) return null;

  const parseMarkdown = (text) => {
    const parts = [];
    const lines = text.split('\n');
    lines.forEach((line, li) => {
      if (li > 0) parts.push(<br key={`br-${li}`} />);
      const segments = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
      segments.forEach((seg, si) => {
        if (seg.startsWith('**') && seg.endsWith('**')) parts.push(<strong key={`${li}-${si}`}>{seg.slice(2, -2)}</strong>);
        else if (seg.startsWith('*') && seg.endsWith('*')) parts.push(<em key={`${li}-${si}`}>{seg.slice(1, -1)}</em>);
        else parts.push(<span key={`${li}-${si}`}>{seg}</span>);
      });
    });
    return parts;
  };

  const sunoMatch = content.match(/Suno\/Udio Prompt[^\n]*\n?([^\n]{20,})/i);
  const hasLyrics = /\[Verse|\[Chorus|\[Bridge|\[Outro|Verse 1:|Chorus:|Bridge:|Pre-Chorus:|V\.O\.|UNCLE EMIT|NARRATION:|SPOKEN WORD/i.test(content);
  const isInstrumentalBrief = sunoMatch && !hasLyrics && /instrumental only|no vocals|pure instrumental/i.test(content);
  const effectivePrompt = hasLyrics ? (sunoMatch ? sunoMatch[1].trim() : storedSunoPromptProp) : isInstrumentalBrief ? sunoMatch[1].trim() : null;
  const sunoPrompt = effectivePrompt || null;

  return (
    <div style={{ lineHeight: '1.7', wordBreak: 'break-word' }}>
      {parseMarkdown(content)}
      {streaming && <span style={{ color: 'rgba(212,175,55,0.6)' }}>▌</span>}

      {!streaming && sunoPrompt && onGenerateMusic && (
        <div style={{ marginTop: '14px', padding: '12px', background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '10px' }}>
          <div style={{ fontSize: '0.8rem', color: 'rgba(212,175,55,0.7)', marginBottom: '8px', letterSpacing: '0.04em' }}>MUSIC GENERATION READY</div>
          <button onClick={() => onGenerateMusic(sunoPrompt, content)} disabled={musicGenerating}
            style={{ padding: '8px 18px', background: musicGenerating ? 'rgba(212,175,55,0.3)' : '#d4af37', border: 'none', borderRadius: '8px', color: '#0a0f1e', fontSize: '0.9rem', fontWeight: '700', cursor: musicGenerating ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
            {musicGenerating ? '⟳ Generating... (~30 sec)' : '🎵 Generate Instrumental Track'}
          </button>
          {musicError && <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#f87171' }}>{musicError}</div>}
          {musicUrl && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: 'rgba(212,175,55,0.7)', marginBottom: '6px' }}>GENERATED TRACK</div>
              <audio controls style={{ width: '100%', accentColor: '#d4af37' }} src={musicUrl} />
              <a href={musicUrl} download="chronicles-track.mp3" style={{ display: 'inline-block', marginTop: '8px', fontSize: '0.8rem', color: '#d4af37', textDecoration: 'none' }}>↓ Download MP3</a>
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: 'rgba(212,175,55,0.6)', letterSpacing: '0.04em' }}>✦ 528 Hz — The Transformation Frequency</div>
            </div>
          )}
        </div>
      )}

      {!streaming && hasLyrics && onGenerateVocals && (
        <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(106,13,173,0.08)', border: '1px solid rgba(106,13,173,0.25)', borderRadius: '10px' }}>
          <div style={{ fontSize: '0.8rem', color: 'rgba(155,48,255,0.8)', marginBottom: '10px', letterSpacing: '0.04em' }}>VOCAL GENERATION</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <select value={selectedCharacter} onChange={(e) => onCharacterChange(e.target.value)}
              style={{ background: '#1a1a2e', color: '#d4af37', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px', padding: '7px 10px', fontSize: '0.9rem', cursor: 'pointer' }}>
              {CHARACTERS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={() => onGenerateVocals(content, messageIndex)} disabled={isGeneratingVocals}
              style={{ padding: '8px 18px', background: isGeneratingVocals ? 'rgba(106,13,173,0.3)' : 'linear-gradient(135deg, #6a0dad, #9b30ff)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', fontWeight: '700', cursor: isGeneratingVocals ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
              {isGeneratingVocals ? '⟳ Generating vocals...' : '🎤 Generate Vocals'}
            </button>
          </div>
          {vocalError && <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#f87171' }}>{vocalError}</div>}
          {vocalUrl && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: 'rgba(155,48,255,0.8)', marginBottom: '6px' }}>🎤 {selectedCharacter} — Vocal Track</div>
              <audio controls style={{ width: '100%', accentColor: '#9b30ff' }} src={vocalUrl} />
              <a href={vocalUrl} download={`vocals-${selectedCharacter.toLowerCase().replace(' ', '-')}.mp3`}
                style={{ display: 'inline-block', marginTop: '8px', fontSize: '0.8rem', color: '#9b30ff', textDecoration: 'none' }}>↓ Download Vocal MP3</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  root: { display: 'flex', flexDirection: 'column', height: '100vh', background: '#0a0f1e', color: '#ffffff', fontFamily: "'Segoe UI', system-ui, sans-serif", position: 'relative', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid rgba(212,175,55,0.15)', background: 'rgba(10,15,30,0.95)', zIndex: 10, flexShrink: 0 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logoIcon: { fontSize: '24px' },
  headerTitle: { fontSize: '1.2rem', fontWeight: '700', color: '#d4af37', letterSpacing: '0.04em' },
  headerSub: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  vectorBadge: { fontSize: '0.8rem', color: '#d4af37', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', padding: '3px 10px', borderRadius: '20px' },
  menuBtn: { background: 'none', border: '1px solid rgba(212,175,55,0.25)', color: '#d4af37', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem' },
  modeBar: { display: 'flex', padding: '10px 20px', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(10,15,30,0.8)', flexShrink: 0 },
  modeBtn: { padding: '10px 22px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', transition: 'all 0.2s' },
  modeBtnActive: { background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37' },
  menuDropdown: { position: 'absolute', top: '100px', right: '20px', zIndex: 100, background: '#0d1530', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '12px', padding: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.6)', minWidth: '220px' },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', padding: '10px 14px', cursor: 'pointer', fontSize: '0.9rem', borderRadius: '8px', transition: 'background 0.15s' },
  menuDivider: { height: '1px', background: 'rgba(255,255,255,0.07)', margin: '6px 8px' },
  historyPanel: { position: 'absolute', top: '100px', right: '20px', zIndex: 100, background: '#0d1530', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '12px', padding: '0', boxShadow: '0 8px 32px rgba(0,0,0,0.6)', width: '320px', maxHeight: '420px', overflowY: 'auto' },
  historyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', fontSize: '0.85rem', fontWeight: '600', color: '#d4af37' },
  historyClose: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1rem' },
  historyEmpty: { padding: '20px 16px', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', textAlign: 'center' },
  historyItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' },
  historyItemPreview: { fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', marginBottom: '4px', lineHeight: 1.4 },
  historyItemMeta: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  messagesArea: { flex: 1, overflowY: 'auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center', margin: 'auto' },
  emptyIcon: { fontSize: '52px', marginBottom: '20px', color: 'rgba(212,175,55,0.5)' },
  emptyTitle: { fontSize: '1.4rem', fontWeight: '600', color: 'rgba(255,255,255,0.85)', marginBottom: '12px' },
  emptyDesc: { fontSize: '1rem', color: 'rgba(255,255,255,0.45)', maxWidth: '400px', lineHeight: 1.6 },
  msgRow: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  avatarIcon: { fontSize: '20px', marginTop: '4px', flexShrink: 0 },
  bubble: { maxWidth: '78%', padding: '12px 16px', borderRadius: '14px', fontSize: '1rem', lineHeight: '1.7' },
  bubbleUser: { background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.2)', color: '#ffffff', borderTopRightRadius: '4px' },
  bubbleAssistant: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.9)', borderTopLeftRadius: '4px' },
  bubbleError: { background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.2)' },
  inputWrapper: { flexShrink: 0, padding: '12px 16px 8px', borderTop: '1px solid rgba(212,175,55,0.1)', background: 'rgba(10,15,30,0.95)' },
  textarea: { width: '100%', padding: '12px 16px', resize: 'none', minHeight: '52px', maxHeight: '160px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '10px', color: '#ffffff', fontSize: '1rem', fontFamily: 'inherit', outline: 'none', lineHeight: '1.5', overflowY: 'auto', boxSizing: 'border-box' },
  buttonRow: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', paddingBottom: '2px' },
  iconBtn: { width: '48px', height: '48px', borderRadius: '50%', cursor: 'pointer', fontSize: '20px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s', border: 'none' },
  sendBtn: { width: '56px', height: '48px', background: '#d4af37', border: 'none', borderRadius: '12px', color: '#0a0f1e', fontSize: '1.3rem', fontWeight: '700', cursor: 'pointer', flexShrink: 0, transition: 'opacity 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  listeningIndicator: { marginTop: '8px', textAlign: 'center', color: '#f43f5e', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  listeningDot: { width: '8px', height: '8px', background: '#f43f5e', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite' },
  inputHint: { textAlign: 'center', paddingTop: '6px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.2)' },
  playbackBar: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', padding: '8px 12px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '10px' },
  playbackBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '18px', transition: 'background 0.2s' },
  playbackLabel: { fontSize: '0.8rem', color: 'rgba(212,175,55,0.7)', marginLeft: '4px', letterSpacing: '0.04em' },
};
